import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';
import App from './app/app';
import { AuthProvider } from './app/auth';
import { Loader } from './components/Loader';
import i18nInitPromise from './i18n';
import { queryClient } from './network/queryClient';
import { Error as ErrorPage } from './pages/Error';
import { isChunkLoadError } from './routes/utils';
import { ConfigProvider, loadDeploymentConfig } from './services/config';
import { initSentry } from './services/sentry/sentry';
import { isAirgappedMode } from './utils/env';

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

export const AppWithProviders = () => (
  <ConfigProvider>
    <ErrorBoundary
      fallback={<ErrorPage />}
      onError={(err) => {
        if (!isChunkLoadError(err) || !navigator.onLine) return;

        try {
          if (sessionStorage.getItem(SESSION_STORAGE_KEYS.DID_CHUNK_RELOAD))
            return;

          sessionStorage.setItem(SESSION_STORAGE_KEYS.DID_CHUNK_RELOAD, 'true');
        } catch {
          // sessionStorage unavailable — treat as first attempt and reload anyway
        }

        setTimeout(() => window.location.reload(), 100);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </ConfigProvider>
);

/**
 * Fatal boot screen for an unresolvable deployment config (e.g. a production
 * build with missing config injection — see DEPLOYMENT_PLAN.md). Rendered instead
 * of the app so no API call is ever made against a bad server_url. Intentionally
 * not i18n'd: it must render without config, i18n, or any provider, and should
 * never reach end users in a correctly configured deployment.
 */
const renderFatalConfigError = (
  root: ReturnType<typeof createRoot>,
  error: unknown,
): void => {
  const message =
    error instanceof Error ? error.message : 'Unknown configuration error';

  root.render(
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '12px',
        padding: '24px',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        color: '#e2e8f0',
        background: '#0b0f19',
      }}
    >
      <h1 style={{ fontSize: '20px', margin: 0 }}>Configuration error</h1>
      <p style={{ maxWidth: '640px', margin: 0, opacity: 0.8 }}>{message}</p>
    </div>,
  );
};

const initializeApp = async () => {
  const container = document.getElementById('root');

  if (!container) {
    throw new Error('No root element found');
  }

  const root = createRoot(container);

  root.render(<Loader center />);

  try {
    await Promise.all([
      // i18n failure is non-fatal — render with fallback keys rather than hanging on the loader
      i18nInitPromise.catch(() => {}),
      loadDeploymentConfig(),
    ]);
  } catch (error) {
    console.error('[bootstrap] fatal deployment config error', error);
    renderFatalConfigError(root, error);

    return;
  }

  if (!isAirgappedMode()) {
    initSentry();
  }

  root.render(<AppWithProviders />);
};

// Only initialize the app if we're not in a test environment
if (import.meta.env.MODE !== 'test') {
  initializeApp();
}
